import React, { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, MapPin, Clock, Banknote, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { getCityBySlug, CITIES, cityImage } from "../data/cities";
import { generateArticle, articleWordCount } from "../lib/blogContent";
import { computeFare } from "../data/pricing";
import { BRAND } from "../mock";
import { useSeo, SEO_BASE } from "../lib/seo";
import { useLang } from "../i18n/LanguageContext";
import BookingWidget from "../components/BookingWidget";

const Article = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const citySlug = (slug || "").replace(/^flughafentransfer-/, "");
  const city = getCityBySlug(citySlug);

  const article = useMemo(() => (city ? generateArticle(city, lang) : null), [city, lang]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) document.title = article.title;
  }, [article]);

  useEffect(() => {
    if (!city) navigate("/ratgeber", { replace: true });
  }, [city, navigate]);

  useSeo(
    city && article
      ? {
          title: article.title,
          description: article.description,
          path: `/flughafentransfer-${city.slug}`,
          image: cityImage(city),
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "TaxiService",
              name: article.h1,
              serviceType: "Flughafentransfer",
              areaServed: { "@type": "City", name: city.name },
              provider: {
                "@type": "LocalBusiness",
                name: BRAND.name,
                telephone: BRAND.phone,
                email: BRAND.email,
                address: { "@type": "PostalAddress", addressLocality: "Kloten", postalCode: "8302", addressCountry: "CH" },
              },
              offers: { "@type": "Offer", price: computeFare("economy", city.km), priceCurrency: "CHF" },
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: article.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SEO_BASE + "/" },
                { "@type": "ListItem", position: 2, name: "Ratgeber", item: SEO_BASE + "/ratgeber" },
                { "@type": "ListItem", position: 3, name: article.h1, item: `${SEO_BASE}/flughafentransfer-${city.slug}` },
              ],
            },
          ],
        }
      : {}
  );

  if (!city || !article) return null;

  const words = articleWordCount(article);
  const readMin = Math.max(6, Math.round(words / 200));
  const related = CITIES.filter((c) => c.canton === city.canton && c.slug !== city.slug).slice(0, 3);
  const fallback = CITIES.filter((c) => c.slug !== city.slug).slice(0, 3);
  const relatedList = related.length ? related : fallback;

  const HERO_IMGS = [
    "https://images.unsplash.com/photo-1715267174266-95519fabe0c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "https://customer-assets.emergentagent.com/job_taxi-zurich-flight/artifacts/yu4v2g3o_IMG_2659.jpeg",
    "https://customer-assets.emergentagent.com/job_taxi-zurich-flight/artifacts/ugrfnbaa_IMG_2660.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/4a6f1bb5b20a7333c87fce7bbb7941d16e11e75aab59f90160f036b9295b132f.png",
  ];
  const heroImg = cityImage(city) || HERO_IMGS[city.name.length % HERO_IMGS.length];

  return (
    <div className="pt-[68px] min-h-screen bg-white">
      {/* Header */}
      <div className="relative bg-[#0B1533] text-white overflow-hidden">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533] via-[#0B1533]/85 to-[#0B1533]/70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 font-body text-sm text-white/60 hover:text-white transition-colors mb-6"><ArrowLeft size={16} /> {t("blog.back")}</Link>
          <p className="font-body text-sm text-[#C9A24B] font-semibold mb-3">{city.cantonName} · Schweiz</p>
          <h1 className="font-heading font-bold tracking-tight text-3xl sm:text-5xl leading-[1.08]">{article.h1}</h1>
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2 font-body text-sm"><MapPin size={15} className="text-[#2B4BFF]" /> ~{city.km} km</span>
            <span className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2 font-body text-sm"><Clock size={15} className="text-[#2B4BFF]" /> ~{city.min} min</span>
            <span className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2 font-body text-sm"><Banknote size={15} className="text-[#25D366]" /> {t("common.from")} CHF {computeFare("economy", city.km)}.–</span>
            <span className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2 font-body text-sm">{readMin} {t("blog.minRead")}</span>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={`/booking?to=${encodeURIComponent(city.name)}`} className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-[#1E38D8] transition-colors">{t("hero.view")} <ArrowRight size={16} /></Link>
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 border border-white/20 text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-white/10 transition-colors"><Phone size={16} /> {BRAND.phone}</a>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20">
        <BookingWidget defaultTo={city.name + ", Schweiz"} />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-4 gap-10">
        {/* TOC */}
        <aside className="hidden lg:block lg:col-span-1">
          <div className="sticky top-24">
            <p className="font-heading font-semibold text-sm text-[#0B1533] mb-3">{t("blog.tocTitle")}</p>
            <ul className="space-y-2 border-l border-[#E9E5DE]">
              {article.sections.map((s) => (
                <li key={s.id}><a href={`#${s.id}`} className="block font-body text-sm text-[#5A607A] hover:text-[#2B4BFF] pl-3 -ml-px border-l-2 border-transparent hover:border-[#2B4BFF] transition-colors">{s.h}</a></li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Body */}
        <article className="lg:col-span-3">
          {article.sections.map((s) => (
            <section key={s.id} id={s.id} className="mb-9 scroll-mt-24">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0B1533] mb-4">{s.h}</h2>
              {s.paras.map((p, i) => (<p key={i} className="font-body text-[#3d4257] leading-[1.8] mb-4">{p}</p>))}
              {s.table && (
                <div className="overflow-x-auto rounded-2xl border border-[#E9E5DE] mt-5">
                  <table className="w-full text-left">
                    <thead className="bg-[#F5F2ED]"><tr><th className="font-heading text-sm text-[#0B1533] px-4 py-3">Klasse</th><th className="font-heading text-sm text-[#0B1533] px-4 py-3">Fahrzeug</th><th className="font-heading text-sm text-[#0B1533] px-4 py-3">Sitze</th><th className="font-heading text-sm text-[#0B1533] px-4 py-3">Fixpreis</th></tr></thead>
                    <tbody>
                      {s.table.map((r, i) => (
                        <tr key={i} className="border-t border-[#E9E5DE]"><td className="font-body text-sm text-[#0B1533] px-4 py-3 font-medium">{r.name}</td><td className="font-body text-sm text-[#5A607A] px-4 py-3">{r.model}</td><td className="font-body text-sm text-[#5A607A] px-4 py-3">{r.seats}</td><td className="font-body text-sm font-semibold text-[#2B4BFF] px-4 py-3">CHF {r.fare}.–</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          {/* FAQ */}
          <section className="mb-10 scroll-mt-24">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0B1533] mb-4">FAQ</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {article.faq.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="bg-[#F5F2ED] border border-[#E9E5DE] rounded-2xl px-5">
                  <AccordionTrigger className="font-heading font-semibold text-[#0B1533] text-left hover:no-underline py-4">{f.q}</AccordionTrigger>
                  <AccordionContent className="font-body text-[#5A607A] leading-relaxed pb-4">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* CTA */}
          <div className="rounded-3xl bg-[#0B1533] p-8 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <h3 className="font-heading font-bold text-2xl">{t("blog.ctaTitle")} {city.name}?</h3>
              <p className="font-body text-white/60 mt-2">{t("blog.ctaSub")}</p>
            </div>
            <Link to={`/booking?to=${encodeURIComponent(city.name)}`} className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#1E38D8] transition-colors shrink-0">{t("hero.view")} <ArrowRight size={16} /></Link>
          </div>
        </article>
      </div>

      {/* Related */}
      <div className="bg-[#F5F2ED] border-t border-[#E9E5DE]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-heading font-semibold text-xl text-[#0B1533] mb-5">{t("blog.relatedTitle")}</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {relatedList.map((c) => (
              <Link key={c.slug} to={c.path} className="group flex items-center justify-between rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#2B4BFF] transition-colors">
                <span className="font-heading font-semibold text-[#0B1533]">{c.name}</span>
                <ArrowRight size={16} className="text-[#2B4BFF] group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Article;
